import React, { useState } from "react";
import Box from "@mui/material/Box";
import { Card, CardContent, TextField, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import { useGlobalMDAlert } from "context";
import { FP, StatusPill, fpInputSx, fpDataGridSx } from "components/financepro";
import { useCreds, postDO, SEMAFORO, fmtMoneyDO, IntroPanel } from "./common";

const PASOS = [
  { icon: ContentPasteRoundedIcon, t: "Pega las cédulas", d: "Una por línea o separadas por coma, en el recuadro de arriba." },
  { icon: PlaylistAddCheckRoundedIcon, t: "Depura la lista", d: "Cada cédula se consulta contra toda la red en un solo paso." },
  { icon: TableRowsRoundedIcon, t: "Revisa el semáforo", d: "Obtienes una tabla con el riesgo, empresas y atraso por cliente." },
];

// Pestaña "Depuración Masiva": semáforo de riesgo para una lista de cédulas.
export function DepuracionLote() {
  const creds = useCreds();
  const { showAlert } = useGlobalMDAlert();
  const [texto, setTexto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [rows, setRows] = useState([]);

  function depurar() {
    const cedulas = texto
      .split(/[\n,;]+/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (cedulas.length === 0) {
      showAlert({ message: "Pegue al menos una cédula", color: "error", autoHideDuration: 3000 });
      return;
    }
    setCargando(true);
    postDO("/api/dataorbit_consultar_lote", {
      usuario: creds.usuario,
      password: creds.password,
      nombrenegocio: creds.nombrenegocio,
      cedulas,
    })
      .then(({ ok, data }) => {
        if (ok) setRows((data.data || []).map((r, i) => ({ id: i, ...r })));
        else {
          setRows([]);
          showAlert({ message: data.Error || "Error en la depuración", color: "error", autoHideDuration: 3000 });
        }
      })
      .finally(() => setCargando(false));
  }

  const columns = [
    { field: "cedula", headerName: "Cédula", minWidth: 120, flex: 0.7 },
    { field: "nombre", headerName: "Nombre", minWidth: 160, flex: 1 },
    {
      field: "semaforo",
      headerName: "Riesgo",
      minWidth: 130,
      flex: 0.6,
      renderCell: (p) => {
        if (!p.row.encontrado) return <StatusPill label="Sin historial" variant="neutral" />;
        const s = SEMAFORO[p.value] || SEMAFORO.verde;
        return <StatusPill label={s.label} variant={s.variant} />;
      },
    },
    { field: "num_empresas", headerName: "Empresas", minWidth: 90, flex: 0.4, align: "center", headerAlign: "center" },
    { field: "max_dias_atraso", headerName: "Atraso (d)", minWidth: 100, flex: 0.4, align: "center", headerAlign: "center" },
    {
      field: "total_vencido",
      headerName: "Vencido",
      minWidth: 120,
      flex: 0.6,
      renderCell: (p) => fmtMoneyDO(p.value),
    },
  ];

  return (
    <>
      <Card elevation={0} sx={{ mb: 3, borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.85rem", mb: 1.5 }}>
            Pegue las cédulas a depurar (una por línea, o separadas por coma).
          </Typography>
          <TextField
            fullWidth
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={"001-1234567-8\n40212345678\n..."}
            sx={fpInputSx}
          />
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              disabled={cargando}
              startIcon={<PlaylistAddCheckRoundedIcon />}
              onClick={depurar}
              sx={{
                minWidth: { sm: 200 },
                height: 46,
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.4,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              {cargando ? "Depurando..." : "Depurar lista"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {rows.length === 0 && (
        <IntroPanel
          title="Depuración masiva de clientes"
          subtitle="Verifica de una sola vez el riesgo crediticio de una lista completa de cédulas contra toda la red."
          steps={PASOS}
        />
      )}

      {rows.length > 0 && (
        <Card elevation={0} sx={{ borderRadius: 2.5, border: `1px solid ${FP.color.border}`, bgcolor: FP.color.cardBg, boxShadow: FP.shadow.soft, overflow: "hidden" }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                pageSizeOptions={[10, 25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                disableRowSelectionOnClick
                rowHeight={42}
                sx={{ minWidth: { xs: 680, sm: "100%" }, height: 520, ...fpDataGridSx }}
                autoHeight={false}
              />
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
}
